import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { clearAuthSession } from '../../services/authSession';

interface SessionExpiredDialogProps {
  /** Shown in the body; lets the caller explain what failed (optional). */
  detail?: string;
  onDismiss: () => void;
}

/**
 * Non-destructive "your session expired" prompt.
 *
 * The user chooses: **Sign in** (clears the dead tokens and reloads, which lands
 * on the login page) or **Not now** (stays exactly where they are). Nothing
 * navigates on its own — an expiry must never interrupt in-progress typing or a
 * recording.
 *
 * Callers that can have unsent input should guard the sign-in action before it
 * reloads (see `notepad.md` #5/#14).
 */
const SessionExpiredDialog: React.FC<SessionExpiredDialogProps> = ({ detail, onDismiss }) => {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
    >
      <div className="w-full max-w-sm rounded-xl border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-white p-5 shadow-xl">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-500" />
          <div className="min-w-0">
            <h3
              id="session-expired-title"
              className="font-semibold dark:text-claude-darkText text-claude-text"
            >
              Session expired
            </h3>
            <p className="mt-1 text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
              {detail || 'Your sign-in has expired. Sign in again to continue.'}
            </p>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-lg border dark:border-claude-darkBorder border-claude-border px-3 py-1.5 text-sm font-medium dark:text-claude-darkText text-claude-text hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={() => {
              clearAuthSession();
              window.location.reload();
            }}
            className="rounded-lg bg-claude-accent hover:bg-claude-accentHover px-3 py-1.5 text-sm font-medium text-white transition-colors"
          >
            Sign in
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionExpiredDialog;
